import React, { useState, useEffect, useCallback, memo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IRouteName, injectRouter } from '../../interface/route';
import Controller from '../../controllers/languageselect';
import { mapRouteToProps, equals as ArgsEquals } from './arguments';
import type { IRouteLanguageSelectArguments } from '../../controllers/languageselect/interface';
import { injectQueryURL } from '../../interface/util';
import { injectConfigAPI } from '../../interface/config';
import util from '../util';
import { sessionHook } from '@monofe/apiservice';

function LanguageSelectRoute(props: RouteComponentProps<{}>): JSX.Element {
    const query = injectQueryURL();
    const router = injectRouter();
    const configAPI = injectConfigAPI();
    const session = sessionHook.useActiveSession();

    const [route, setRoute] = useState<IRouteLanguageSelectArguments>(
        mapRouteToProps(props, query, session, configAPI),
    );

    const didRequestTourSelect = useCallback(
        async (id: number) => {
            util.session.updateLanguageId(session, id);

            router.push(IRouteName.TourSelect, props.history, {
                ...route,
                languageId: id,
            });
        },
        [route, props, router],
    );

    const didRequestBack = useCallback(() => {
        util.session.updateLanguageId(session, -1);

        if (!router.pop(props.history, route)) {
            /* if we fail to go back, reset the app & go to Launch route */
            router.push(IRouteName.Launch, props.history, route, { replace: true });
        }
    }, [route, props, router]);

    useEffect(() => {
        const newRoute = mapRouteToProps(props, query, session, configAPI);
        if (ArgsEquals(newRoute, route)) {
            return;
        }
        setRoute(newRoute);
    }, [props]);

    useEffect(() => {
        util.session.updateLanguageId(session, -1);
        util.session.updateTourId(session, -1);
    }, [route]);

    /* no active session, /launch has not been triggered, force page reload & session initialization */
    if (!session) {
        props.history.replace('/');
    }

    return (
        <Controller.renderer
            key="LanguageSelectRoute"
            didRequestTourSelect={didRequestTourSelect}
            didRequestBack={didRequestBack}
            route={route}
        />
    );
}

const propsEqual = (prev: RouteComponentProps<{}>, next: RouteComponentProps<{}>) => {
    return (
        prev.location.search === next.location.search &&
        prev.location.pathname === next.location.pathname
    );
};

export default memo(LanguageSelectRoute, propsEqual);
